import { useEffect, useRef, useState } from 'react'
import { api } from '../servizi/api.ts'
import { Bottone, Campo } from '../componenti/base.tsx'

type ControlloIndirizzo = { proposta: string | null; indirizzo: string | null; libero: boolean; motivo: string | null; alternative: string[] }

/**
 * Indirizzo della casella CIDIS (520): proposto da nome e cognome (iniziale e cognome, senza accenti,
 * apostrofi e spazi), liberamente modificabile, controllato mentre si scrive. Finche' chi aggiunge non
 * lo tocca segue il nome; se e' preso, la riga dice da che cosa e propone alternative libere.
 */
export function CampoCasellaCidis({ nome, cognome, valore, cambia, utenteId, errore, esterno = false, etichetta }: {
  nome: string; cognome: string; valore: string
  cambia: (parziale: { indirizzo_cidis?: string; indirizzo_libero?: boolean | null }) => void
  utenteId?: string
  errore?: string
  /** «È esterno?» (533): l'indirizzo di accesso su esterni.cidisonlus.org. */
  esterno?: boolean
  etichetta?: string
}) {
  const [controllo, setControllo] = useState<ControlloIndirizzo | null>(null)
  const ultimaProposta = useRef<string | null>(null)

  useEffect(() => {
    if (!valore.trim() && (!nome.trim() || !cognome.trim())) { setControllo(null); cambia({ indirizzo_libero: null }); return }
    const t = setTimeout(() => {
      api.post<ControlloIndirizzo>('/amministrazione/utenti/indirizzo-cidis',
        { nome, cognome, indirizzo: valore.trim() || null, utente_id: utenteId ?? null, esterno })
        .then((r) => {
          setControllo(r)
          // Finche' l'indirizzo e' vuoto o e' la proposta di prima, segue nome e cognome.
          if (r.proposta && (!valore.trim() || valore === ultimaProposta.current) && r.proposta !== valore) {
            ultimaProposta.current = r.proposta
            cambia({ indirizzo_cidis: r.proposta, indirizzo_libero: null })
            return
          }
          cambia({ indirizzo_libero: r.libero })
        })
        .catch(() => cambia({ indirizzo_libero: null }))
    }, 400)
    return () => clearTimeout(t)
    // `cambia` cambia a ogni disegno: il controllo dipende solo dai valori.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nome, cognome, valore, utenteId, esterno])

  const nota = errore ?? (controllo && !controllo.libero ? controllo.motivo ?? undefined : undefined)
  return (
    <div className="campo-doppia-colonna">
      <Campo etichetta={etichetta ?? (esterno ? 'Indirizzo @esterni' : 'Indirizzo della casella CIDIS')}
             obbligatorio maxLength={120} valore={valore}
             cambia={(v) => cambia({ indirizzo_cidis: v.trim().toLowerCase(), indirizzo_libero: null })}
             segnaposto={controllo?.proposta ?? 'iniziale del nome e cognome'} autoComplete="off" errore={nota}
             aiuto={controllo?.libero ? `Libero: la persona entrerà con ${controllo.indirizzo}.`
               : 'Proposto da nome e cognome; puoi scriverne un altro.'} />
      {controllo && !controllo.libero && controllo.alternative.length > 0 && (
        <div className="casella-cidis-alternative">
          <span className="piccolo tenue">Liberi:</span>
          {controllo.alternative.map((a) => (
            <Bottone key={a} dimensione="piccolo" onClick={() => cambia({ indirizzo_cidis: a, indirizzo_libero: null })}>{a}</Bottone>
          ))}
        </div>
      )}
    </div>
  )
}
